/** 经纬度类型定义 */
import { safeLocalStorage, safeStorageGetItem, safeStorageSetItem } from '@/utils/safeLocalStorage'

export interface LngLat {
    lng: number; // 经度
    lat: number; // 纬度
  }
  
  /** 默认经纬度（北京市朝阳区） */
  export const DEFAULT_LNGLAT: LngLat = {
    lng: 116.48102,
    lat: 39.94961
  };
  
  /** 检查 LocalStorage 是否可用（缓存经纬度） */
  const isLocalStorageAvailable = (): boolean => {
    const storage = safeLocalStorage()
    if (!storage) return false
    try {
      const testKey = '__lnglat_test__'
      storage.setItem(testKey, testKey)
      storage.removeItem(testKey)
      return true
    } catch {
      return false
    }
  }
  
  /** 从缓存获取经纬度 */
  export const getStoredLngLat = (): LngLat => {
    if (!isLocalStorageAvailable()) return DEFAULT_LNGLAT
    const stored = safeStorageGetItem('user_lnglat')
    return stored ? (JSON.parse(stored) as LngLat) : DEFAULT_LNGLAT
  }

  /** 缓存经纬度到 LocalStorage */
  const setStoredLngLat = (lnglat: LngLat) => {
    if (isLocalStorageAvailable()) {
      safeStorageSetItem('user_lnglat', JSON.stringify(lnglat))
    }
  }
  
  /** 并发调用时复用同一次浏览器定位，避免 Header + 页面重复等待 */
  let pendingGeolocation: Promise<LngLat> | null = null;

  const requestBrowserLngLat = (forceRefresh: boolean): Promise<LngLat> => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      console.warn('浏览器不支持地理位置功能');
      return Promise.resolve(DEFAULT_LNGLAT);
    }

    if (typeof window !== 'undefined' && window.isSecureContext === false) {
      console.warn('非安全上下文（非 HTTPS），跳过定位授权，使用默认经纬度');
      return Promise.resolve(DEFAULT_LNGLAT);
    }

    return new Promise((resolve) => {
      let settled = false;
      const finish = (val: LngLat) => {
        if (settled) return;
        settled = true;
        resolve(val);
      };

      const fallbackTimer = setTimeout(() => {
        console.warn('定位超时（外层兜底），使用默认经纬度');
        finish(DEFAULT_LNGLAT);
      }, 6000);

      navigator.geolocation.getCurrentPosition(
        (position) => {
          clearTimeout(fallbackTimer);
          const lnglat = {
            lng: position.coords.longitude,
            lat: position.coords.latitude
          };
          console.log('浏览器定位成功，经纬度：', lnglat);
          setStoredLngLat(lnglat);
          finish(lnglat);
        },
        (error) => {
          clearTimeout(fallbackTimer);
          const errorMsg = {
            1: '用户拒绝授权定位',
            2: '定位信息获取失败',
            3: '定位超时'
          }[error.code] || error.message;
          console.error('定位失败：', errorMsg);
          finish(DEFAULT_LNGLAT);
        },
        {
          enableHighAccuracy: false,
          timeout: 5000,
          maximumAge: forceRefresh ? 0 : 3600000
        }
      );
    });
  };

  /**
   * 浏览器原生授权获取经纬度（核心方法）
   * @param forceRefresh 是否强制刷新（忽略缓存）
   */
  export const getBrowserLngLat = async (forceRefresh = false): Promise<LngLat> => {
    if (!forceRefresh && isLocalStorageAvailable()) {
      const raw = safeStorageGetItem('user_lnglat')
      if (raw) {
        try { return JSON.parse(raw) as LngLat; } catch (_) { /* 缓存损坏则继续走授权流程 */ }
      }
    }

    if (forceRefresh) {
      return requestBrowserLngLat(true);
    }

    if (pendingGeolocation) {
      return pendingGeolocation;
    }

    pendingGeolocation = requestBrowserLngLat(false).finally(() => {
      pendingGeolocation = null;
    });
    return pendingGeolocation;
  };